import { FC } from 'react';
import type { AssessmentProgressDocument } from '@libs/types';
import { Box, LinearProgress, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { type SingularKeys, useTrans } from '@libs/i18n';
import {
  CheckCircleIcon,
  ClockIcon,
  FileTextIcon,
  MinusIcon,
  WarningIcon,
  XCircleIcon,
} from '@phosphor-icons/react';

import {
  deriveDocumentRowState,
  documentGaps,
  type DocumentGroup,
  type DocumentRowState,
  gradingPercent,
  type RunView,
} from '../../utils/run-progress';

/**
 * The document table.
 *
 * Four columns — document, status, progress, gaps — because those are the four
 * questions someone watching a run actually has. The gaps column is empty for
 * every row that is not fully assessed, and that emptiness is load-bearing: see
 * `documentGaps`.
 */

const GRID = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'minmax(0, 1.6fr) minmax(0, 110px)',
    md: 'minmax(0, 1.7fr) minmax(0, 130px) minmax(0, 1fr) minmax(0, 1fr)',
  },
  gap: 2,
  alignItems: 'center',
} as const;

const STATE_LABEL_KEYS: Record<DocumentRowState, SingularKeys> = {
  queued: 'app.assessment.progress.state.queued',
  reading: 'app.assessment.progress.state.reading',
  read: 'app.assessment.progress.state.read',
  classified: 'app.assessment.progress.state.classified',
  grading: 'app.assessment.progress.state.grading',
  graded: 'app.assessment.progress.state.graded',
  failed: 'app.assessment.progress.state.failed',
  nodata: 'app.assessment.progress.state.nodata',
  stalled: 'app.assessment.progress.state.lastSeen',
};

const FAILURE_KEYS: Record<string, SingularKeys> = {
  unreadable: 'app.assessment.progress.failure.unreadable',
  empty: 'app.assessment.progress.failure.empty',
  extraction_failed: 'app.assessment.progress.failure.extraction_failed',
  grading_failed: 'app.assessment.progress.failure.grading_failed',
  unknown: 'app.assessment.progress.failure.unknown',
};

export const DocumentListCard: FC<{
  groups: DocumentGroup[];
  runView: RunView;
  meta: string;
  footerNote: string;
}> = observer(({ groups, runView, meta, footerNote }) => {
  const theme = useTheme();
  const { t } = useTrans();

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          backgroundColor: theme.palette.grey[200],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
          {t('app.assessment.progress.documents')}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{meta}</Typography>
      </Box>

      <Box
        sx={{
          ...GRID,
          px: 2.5,
          py: 1.25,
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
        }}
      >
        {(
          [
            'app.assessment.progress.colDocument',
            'app.assessment.progress.colStatus',
            'app.assessment.progress.colProgress',
            'app.assessment.progress.colGaps',
          ] as const
        ).map((key, index) => (
          <Typography
            key={key}
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.4px',
              textTransform: 'uppercase',
              color: 'text.secondary',
              display: index > 1 ? { xs: 'none', md: 'block' } : 'block',
            }}
          >
            {t(key)}
          </Typography>
        ))}
      </Box>

      {groups.length === 0 && (
        <Typography sx={{ px: 2.5, py: 3, fontSize: '0.875rem', color: 'text.secondary' }}>
          {t('app.assessment.progress.emptyDocuments')}
        </Typography>
      )}

      {groups.map((group) => (
        <Box key={group.domainKey}>
          <Box
            sx={{
              px: 2.5,
              py: 1,
              backgroundColor: theme.palette.grey[200],
              borderTop: `1px solid ${theme.palette.grey[300]}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.03em' }}>
              {group.domainName}
            </Typography>
          </Box>
          {group.documents.map((doc) => (
            <DocumentRow key={doc.documentId} document={doc} runView={runView} />
          ))}
        </Box>
      ))}

      <Box
        sx={{
          borderTop: `1px solid ${theme.palette.grey[300]}`,
          px: 2.5,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Typography sx={{ fontSize: '0.75rem', color: 'text.tertiary' }}>{footerNote}</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.tertiary' }}>
          {t('app.assessment.progress.leavePage')}
        </Typography>
      </Box>
    </Box>
  );
});

const DocumentRow: FC<{ document: AssessmentProgressDocument; runView: RunView }> = observer(
  ({ document: doc, runView }) => {
    const theme = useTheme();
    const { t } = useTrans();
    const state = deriveDocumentRowState(doc, runView);
    const gaps = documentGaps(doc);

    const minutes =
      doc.startedAt && !doc.completedAt
        ? null
        : doc.startedAt && doc.completedAt
          ? Math.max(
              1,
              Math.round(
                (new Date(doc.completedAt).getTime() - new Date(doc.startedAt).getTime()) / 60_000,
              ),
            )
          : null;

    return (
      <Box
        sx={{
          ...GRID,
          px: 2.5,
          py: 1.625,
          borderTop: `1px solid ${theme.palette.grey[200]}`,
          backgroundColor: state === 'failed' ? `${theme.palette.error.main}0D` : 'transparent',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <StateIcon state={state} gaps={gaps} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={doc.fileName}
            >
              {doc.fileName}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.tertiary', mt: 0.125 }}>
              {state === 'failed'
                ? t(FAILURE_KEYS[doc.failureReason ?? 'unknown'] ?? FAILURE_KEYS.unknown)
                : minutes != null
                  ? t('app.assessment.progress.note.minutesOnDocument', { minutes })
                  : ''}
            </Typography>
          </Box>
        </Box>

        <StateBadge state={state} />

        <Box sx={{ minWidth: 0, display: { xs: 'none', md: 'block' } }}>
          <ProgressCell document={doc} state={state} />
        </Box>

        <Box sx={{ minWidth: 0, display: { xs: 'none', md: 'block' } }}>
          <GapsCell document={doc} state={state} gaps={gaps} />
        </Box>
      </Box>
    );
  },
);

const StateIcon: FC<{ state: DocumentRowState; gaps: number | null }> = observer(
  ({ state, gaps }) => {
    const theme = useTheme();

    if (state === 'reading' || state === 'grading') {
      return <ClockIcon size={18} color={theme.palette.ocean[700]} />;
    }
    if (state === 'stalled') {
      return <ClockIcon size={18} color={theme.palette.yellow[800]} />;
    }
    if (state === 'nodata') {
      return <MinusIcon size={18} color={theme.palette.text.tertiary} />;
    }
    if (state === 'failed') {
      return <XCircleIcon size={18} weight="fill" color={theme.palette.error.main} />;
    }
    if (state === 'graded') {
      return gaps === 0 ? (
        <CheckCircleIcon size={18} weight="fill" color={theme.palette.accent.green} />
      ) : (
        <WarningIcon size={18} color={theme.palette.error.main} />
      );
    }
    if (state === 'read' || state === 'classified') {
      return <FileTextIcon size={18} color={theme.palette.text.secondary} />;
    }
    return <ClockIcon size={18} color={theme.palette.text.tertiary} />;
  },
);

const StateBadge: FC<{ state: DocumentRowState }> = observer(({ state }) => {
  const theme = useTheme();
  const { t } = useTrans();

  const tone = ((): { bg: string; color: string } | undefined => {
    switch (state) {
      case 'reading':
      case 'grading':
        return { bg: theme.palette.ocean[100], color: theme.palette.ocean[700] };
      case 'graded':
        return {
          bg: `${theme.palette.accent.green}1A`,
          color: theme.palette.accent.green,
        };
      case 'failed':
        return { bg: `${theme.palette.error.main}1A`, color: theme.palette.error.main };
      case 'stalled':
        return { bg: theme.palette.yellow[200], color: theme.palette.yellow[900] };
      default:
        return { bg: theme.palette.grey[200], color: theme.palette.text.secondary };
    }
  })();

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignSelf: 'flex-start',
        px: 1,
        py: 0.375,
        borderRadius: 1,
        fontSize: 11.5,
        fontWeight: 600,
        backgroundColor: tone?.bg,
        color: tone?.color,
        whiteSpace: 'nowrap',
      }}
    >
      {t(STATE_LABEL_KEYS[state])}
    </Box>
  );
});

const ProgressCell: FC<{ document: AssessmentProgressDocument; state: DocumentRowState }> =
  observer(({ document: doc, state }) => {
    const theme = useTheme();
    const { t } = useTrans();

    // How far through, not how many of what: `gradingPercent` is the only
    // figure this cell will print, because section counts are not comparable
    // between two documents sitting in the same table.
    const percent = gradingPercent(doc);

    if (state === 'grading' && percent !== null) {
      return (
        <Box>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, mb: 0.5 }}>
            {t('app.assessment.progress.note.percentComplete', { percent })}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={percent}
            sx={{
              height: 6,
              borderRadius: 9999,
              backgroundColor: theme.palette.grey[300],
              '& .MuiLinearProgress-bar': { backgroundColor: theme.palette.ocean[700] },
            }}
          />
        </Box>
      );
    }

    if (state === 'reading' || state === 'grading') {
      // Indeterminate on purpose. Extraction reports no intermediate progress,
      // and a grader that has not sized the document yet has none either, so a
      // determinate value here would be a number we made up.
      return (
        <LinearProgress
          sx={{
            height: 6,
            borderRadius: 9999,
            backgroundColor: theme.palette.grey[300],
            '& .MuiLinearProgress-bar': { backgroundColor: theme.palette.ocean[700] },
          }}
        />
      );
    }

    const note = ((): string => {
      // A stalled row that never reported a section has no last-seen figure to
      // give, and 0 of 0 was never one — the badge beside it already says the
      // run went quiet.
      if (state === 'stalled') {
        return percent === null
          ? '—'
          : t('app.assessment.progress.note.lastSeenPercent', { percent });
      }
      if (state === 'graded') return t('app.assessment.progress.note.allSectionsDone');
      if (state === 'read') return t('app.assessment.progress.note.waitingToMatch');
      if (state === 'classified') return t('app.assessment.progress.note.waitingToGrade');
      if (state === 'failed') return t('app.assessment.progress.note.notAssessed');
      if (state === 'nodata') return '—';
      return t('app.assessment.progress.note.notStarted');
    })();

    return <Typography sx={{ fontSize: '0.75rem', color: 'text.tertiary' }}>{note}</Typography>;
  });

const GapsCell: FC<{
  document: AssessmentProgressDocument;
  state: DocumentRowState;
  gaps: number | null;
}> = observer(({ document: doc, state, gaps }) => {
  const theme = useTheme();
  const { t } = useTrans();

  if (state === 'failed') {
    return (
      <Typography sx={{ fontSize: '0.75rem', color: theme.palette.error.main }}>
        {t('app.assessment.progress.note.willRetry')}
      </Typography>
    );
  }

  if (gaps == null) {
    return <Typography sx={{ fontSize: '0.875rem', color: 'text.disabled' }}>—</Typography>;
  }

  // A partially graded document's figure is a floor, not a total: some questions
  // could not be resolved, and every one of them could turn out to be a gap. It
  // is shown as `≤ n` in a bordered chip so it can never be read as a clean
  // count sitting next to the clean counts above it.
  if (doc.partiallyGraded) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          flexDirection: 'column',
          gap: 0.125,
          border: `1px dashed ${theme.palette.yellow[800]}`,
          backgroundColor: theme.palette.yellow[200],
          borderRadius: 1,
          px: 1,
          py: 0.5,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.9375rem',
            fontWeight: 700,
            color: theme.palette.yellow[1000],
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {`≤ ${gaps}`}
        </Typography>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
            color: theme.palette.yellow[900],
          }}
        >
          {t('app.assessment.progress.lowerBound')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        sx={{
          fontSize: '0.9375rem',
          fontWeight: 700,
          color: gaps === 0 ? theme.palette.green[800] : theme.palette.error.main,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {gaps}
      </Typography>
      <Typography sx={{ fontSize: 11, color: 'text.tertiary' }}>
        {t('app.assessment.progress.gapsFound')}
      </Typography>
    </Box>
  );
});
